import { useStanQuery } from '.';
import { getLoginSessions } from '@/lib/api/settings/api';
import { QUERY_KEYS } from '@/lib';
import { formatAsRelativeTime } from '@/lib/date';
import { formatDeviceName, formatGeoLocation } from '@/features/settings/lib/security.logic';
import type { DisplayLoginSession } from '@/features/settings/lib/security.types';
import type { LoginSession } from '@/lib/api/settings/types';

/**
 * Transform API login session data to display format
 * Only includes active sessions (status === 1)
 */
function transformLoginSessions(apiSessions: LoginSession[]): DisplayLoginSession[] {
    // Filter to only show active sessions (status === 1)
    const activeSessions = apiSessions.filter(session => session.status === 1);

    return activeSessions.map((session, index) => ({
        id: session.id,
        device: formatDeviceName(session.device),
        location: formatGeoLocation(session.data['ip-geo-location']),
        ipAddress: session.ip,
        loginTime: formatAsRelativeTime(session.created_at),
        current: index === 0, // Assume first session is current
    }));
}

/**
 * Hook to fetch and transform login sessions
 * Data is cached for 5 minutes
 */
export function useLoginSessions() {
    const {
        data: loginSessions = [],
        isLoading,
        error,
        refetch,
    } = useStanQuery<DisplayLoginSession[]>({
        queryKey: [QUERY_KEYS.LOGIN_SESSIONS],
        queryFn: async () => {
            const apiSessions = await getLoginSessions();
            return transformLoginSessions(apiSessions);
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        meta: {
            error: {
                sentryMessage: '[LOGIN_SESSIONS] failed-to-fetch-login-sessions',
                title: 'Error',
                message: 'Failed to load login sessions. Please try again later.',
            },
        },
    });

    return {
        loginSessions,
        isLoading,
        error,
        refetch,
    };
}
